<template>
  <v-dialog v-model="visible" max-width="600" persistent>
    <v-toolbar color="secondary" flat density="compact">
      <v-toolbar-title class="d-flex align-center">
        <v-icon class="text-accent mr-2">mdi-transfer</v-icon>
        <span class="text-h6 text-accent"
          >{{ $t("machines.dialogs.transferMachine.title") }}
          {{ machine.serialNo }}
        </span>
      </v-toolbar-title>
    </v-toolbar>

    <v-card v-if="existingTransfer !== null">
      <v-card-title class="text-h5 text-black">{{
        $t("machines.dialogs.transferMachine.steps.transferOwnership.title")
      }}</v-card-title>
      <v-card-text class="text-primary">
        <div v-if="existingTransfer.transferred !== undefined">
          <p>
            {{
              $t(
                "machines.dialogs.transferMachine.errors.machineAlreadyTransferred"
              )
            }}
            {{ $filters.localeDateFilter(existingTransfer.transferred) }}
          </p>
          <p>{{ $t("machines.dialogs.transferMachine.refreshTheList") }}</p>
        </div>
        <div v-else>
          <p>
            {{ $t("machines.dialogs.transferMachine.errors.pendingTransfer") }}
            {{ $filters.localeDateFilter(existingTransfer.created) }}
          </p>
          <v-text-field
            variant="outlined"
            readonly
            :label="$t('machines.dialogs.transferMachine.transferCode')"
            v-model="existingTransfer.transferCode"
          ></v-text-field>
          <p>
            {{
              $t("machines.dialogs.transferMachine.errors.transferCodeExpires")
            }}
            {{ $filters.localeDateFilter(existingTransfer.expires) }}
          </p>
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="onCancel">{{ $t("common.actions.close") }}</v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>

    <v-stepper v-else v-model="stepNo" hide-actions>
      <v-stepper-header>
        <v-stepper-item
          :complete="stepNo > 1"
          :value="1"
          :title="
            $t('machines.dialogs.transferMachine.steps.transferOwnership.name')
          "
        ></v-stepper-item>
        <v-divider></v-divider>
        <v-stepper-item
          :complete="stepNo > 2"
          :value="2"
          :title="$t('machines.dialogs.transferMachine.steps.dataRetention.name')"
        ></v-stepper-item>
        <v-divider></v-divider>
        <v-stepper-item
          :value="3"
          :complete="stepNo > 3"
          :title="$t('machines.dialogs.transferMachine.steps.confirm.name')"
        ></v-stepper-item>
        <v-divider></v-divider>
        <v-stepper-item
          :value="4"
          :complete="newTransfer !== null"
          :title="$t('machines.dialogs.transferMachine.steps.summary.name')"
        ></v-stepper-item>
      </v-stepper-header>

      <v-stepper-window>
        <v-stepper-window-item :value="1">
          <v-card flat>
            <v-card-title class="text-h5 text-black">
              {{
                $t(
                  "machines.dialogs.transferMachine.steps.transferOwnership.title"
                )
              }}
            </v-card-title>
            <v-card-text class="text-primary">
              <p>
                {{
                  $t(
                    "machines.dialogs.transferMachine.steps.transferOwnership.description"
                  )
                }}
              </p>
              <p>
                {{
                  $t(
                    "machines.dialogs.transferMachine.transferAutomaticRevertInfo"
                  )
                }}
              </p>
            </v-card-text>
          </v-card>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="stepNo = 2">{{
              $t("common.actions.next")
            }}</v-btn>
            <v-btn
              variant="text"
              @click="onCancel"
              class="grab-cancel-machine-transfer"
              >{{ $t("common.actions.cancel") }}</v-btn
            >
          </v-card-actions>
        </v-stepper-window-item>

        <v-stepper-window-item :value="2">
          <v-card flat>
            <v-card-title class="text-h5 text-black">
              {{
                $t("machines.dialogs.transferMachine.steps.dataRetention.title")
              }}
            </v-card-title>
            <v-card-text class="text-primary">
              <p>
                {{
                  $t(
                    "machines.dialogs.transferMachine.steps.dataRetention.description"
                  )
                }}
              </p>
              <v-checkbox v-model="includeHistoryChecked">
                <template v-slot:label>
                  <div>
                    {{
                      $t(
                        "machines.dialogs.transferMachine.steps.dataRetention.includeHistoricalData"
                      )
                    }}
                  </div>
                </template>
              </v-checkbox>
            </v-card-text>
          </v-card>
          <v-card-actions>
            <v-btn variant="text" @click="stepNo = 1">{{
              $t("common.actions.previous")
            }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="stepNo = 3">{{
              $t("common.actions.next")
            }}</v-btn>
            <v-btn variant="text" @click="onCancel">{{
              $t("common.actions.cancel")
            }}</v-btn>
          </v-card-actions>
        </v-stepper-window-item>

        <v-stepper-window-item :value="3">
          <v-card flat>
            <v-card-title class="text-h5 text-black">
              {{ $t("machines.dialogs.transferMachine.steps.confirm.title") }}
            </v-card-title>
            <v-card-text class="text-primary">
              <p>
                {{
                  $t(
                    "machines.dialogs.transferMachine.steps.confirm.description",
                    {
                      sn: machine.serialNo,
                      productName: machine.productName,
                      tenant: machine.tenantName
                    }
                  )
                }}
              </p>
              <p>
                {{
                  includeHistoryChecked
                    ? $t(
                        "machines.dialogs.transferMachine.steps.confirm.includeHistoricalDataInfo"
                      )
                    : $t(
                        "machines.dialogs.transferMachine.steps.confirm.notIncludeHistoricalDataInfo"
                      )
                }}
              </p>
              <p>
                {{
                  $t(
                    "machines.dialogs.transferMachine.steps.confirm.proceedInfo"
                  )
                }}
              </p>
            </v-card-text>
          </v-card>
          <v-card-actions>
            <v-btn
              variant="text"
              :disabled="isGeneratingCode"
              @click="stepNo = 2"
              >{{ $t("common.actions.previous") }}</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn variant="text" :loading="isGeneratingCode" @click="onSave">{{
              $t("common.actions.next")
            }}</v-btn>
            <v-btn
              variant="text"
              :disabled="isGeneratingCode"
              @click="onCancel"
              >{{ $t("common.actions.cancel") }}</v-btn
            >
          </v-card-actions>
        </v-stepper-window-item>

        <v-stepper-window-item :value="4">
          <v-card flat>
            <v-card-title class="text-h5 text-black">
              {{ $t("machines.dialogs.transferMachine.steps.summary.title") }}
            </v-card-title>
            <v-card-text class="text-primary" v-if="errorMsg !== null">
              <p>{{ errorMsg }}</p>
            </v-card-text>
            <v-card-text class="text-primary" v-else
              ><p>
                {{
                  $t(
                    "machines.dialogs.transferMachine.steps.summary.description"
                  )
                }}
              </p>
              <v-text-field
                v-if="newTransfer !== null"
                variant="outlined"
                readonly
                :label="$t('machines.dialogs.transferMachine.transferCode')"
                v-model="newTransfer.transferCode"
              ></v-text-field>
              <p>
                {{
                  $t(
                    "machines.dialogs.transferMachine.transferAutomaticRevertInfo"
                  )
                }}
              </p>
            </v-card-text>
          </v-card>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="onCancel">{{
              $t("common.actions.close")
            }}</v-btn>
          </v-card-actions>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>
  </v-dialog>
</template>

<script>
export default {
  name: "GuidedMachineTransfer",
  props: ["machine", "show"],
  emits: ["cancel"],
  data() {
    return {
      stepNo: 1,
      existingTransfer: null,
      newTransfer: null,
      isGeneratingCode: false,
      includeHistoryChecked: false,
      errorMsg: null
    }
  },
  created() {},
  computed: {
    visible: {
      get() {
        return this.show
      },
      set(value) {
        if (!value) {
          this.$emit("cancel")
        }
      }
    }
  },
  watch: {
    show() {
      this.stepNo = 1
      this.existingTransfer = null
      this.newTransfer = null
      this.isGeneratingCode = false
      this.includeHistoryChecked = false
      this.errorMsg = null

      this.fetchData()
    }
  },
  methods: {
    fetchData() {
      if (this.machine !== undefined && this.machine !== null) {
        this.$store
          .dispatch("fetchMachineTransfer", {
            tenantUid: this.machine.tenantUid,
            serialNo: this.machine.serialNo
          })
          .then(machineTransfer => {
            this.existingTransfer = machineTransfer
          })
          .catch(() => {
            this.existingTransfer = null
          })
      }
    },
    onSave() {
      this.isGeneratingCode = true
      this.errorMsg = null

      const params = {
        machine: this.machine,
        transfer: {
          includeHistory: this.includeHistoryChecked,
          srcTenantUid: this.machine.tenantUid
        }
      }

      this.$store
        .dispatch("createMachineTransfer", params)
        .then(() => {
          this.newTransfer = this.$store.getters.machineTransfer
          this.stepNo = 4
        })
        .catch(() => {
          this.errorMsg = this.$t(
            "machines.dialogs.transferMachine.errors.internalError"
          )
          this.stepNo = 4
        })
        .finally(() => {
          this.isGeneratingCode = false
        })
    },
    onCancel() {
      this.visible = false
    }
  }
}
</script>
